import React from 'react';
import { reportClientError } from '../utils/errorReporter';

interface Props { children: React.ReactNode }
interface State { hasError: boolean }

/**
 * App-level error boundary — catches React render crashes, reports them to the backend
 * (so they show up in Cloud Error Reporting), and shows a minimal recovery screen instead
 * of a blank white page.
 */
class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Send BOTH the JS stack AND the React component stack. The component stack ("in AdminCostPage in
    // Route in App …") pinpoints WHICH component threw — far more useful than a minified prod JS stack —
    // so the admin Errors view can actually locate the problem.
    const parts = [error?.stack, info?.componentStack ? `Component stack:${info.componentStack}` : ''].filter(Boolean);
    reportClientError({
      message: error?.message || 'React render error',
      stack: parts.join('\n\n') || undefined,
      kind: 'react',
    });
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white dark:bg-black text-center px-6">
        <p className="text-lg font-semibold text-secondary-900 dark:text-white">Something went wrong</p>
        <p className="mt-1 text-sm text-secondary-500">We've logged the issue. Try reloading the page.</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 rounded-xl bg-secondary-900 dark:bg-white px-4 py-2 text-sm font-medium text-white dark:text-secondary-900"
        >
          Reload
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
